/**
 * AlternativePlanBanner
 *
 * Shared "you're on the Alternative plan" indicator, used by both the wizard
 * (where the user is editing the alternative) and the projection results page
 * (where previously the only cue was a page-title accent and an easy-to-miss
 * sidebar pill — no persistent banner). See AlternativePlanBanner.css for why
 * this isn't position:sticky.
 */

import type { ReactNode } from 'react';
import './AlternativePlanBanner.css';

interface AlternativePlanBannerProps {
  /** 'editing' (wizard) vs 'viewing' (projection results) changes the copy. */
  variant: 'editing' | 'viewing';
  /** Optional trailing controls, e.g. the wizard's "Reset to My Plan" buttons. */
  actions?: ReactNode;
}

const VARIANT_TEXT: Record<AlternativePlanBannerProps['variant'], string> = {
  editing: "Editing Alternative Plan — changes here won't affect your primary plan",
  viewing: 'Viewing Alternative Plan — this is a what-if scenario, not your primary plan',
};

export function AlternativePlanBanner({ variant, actions }: Readonly<AlternativePlanBannerProps>) {
  return (
    <div className="alt-plan-banner">
      <span className="alt-plan-banner-icon" aria-hidden="true">&#9888;</span>
      <span className="alt-plan-banner-text" role="status">{VARIANT_TEXT[variant]}</span>
      {actions}
    </div>
  );
}
